import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, DatePicker, Button, message } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Header from '../../Shared/Header';
import backIcon from "../../../assets/icons/backIcon.svg";
import { getLeafAnalysisInfo, updateLeafAnalysis } from '../../../Utils/analysis/leafAnalysis';
import moment from 'moment';
import Loading from '../../Shared/Loading';
import CustomDatePicker from '../../Shared/CustomDatePicker';




const EditLeafAnalysis = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { exploitationId, analysisId } = useParams();
    const [loading, setLoading] = useState(false);
    const [initialData, setInitialData] = useState(null);

    useEffect(() => {
        const fetchAnalysis = async () => {
            try {
                const data = (await getLeafAnalysisInfo(analysisId)).data;
                setInitialData(data);
                form.setFieldsValue({
                    ...data,
                    date_realisation: moment(data.date_realisation),
                });
            } catch (error) {
                console.error('Erreur lors de la récupération de l\'analyse:', error);
                message.error('Échec de la récupération de l\'analyse');
            }
        };

        fetchAnalysis();
    }, [analysisId, form]);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const formattedValues = {
                ...values,
                date_realisation: values.date_realisation.toISOString(),
                exploitation_id: parseInt(exploitationId),
            };
            await updateLeafAnalysis(analysisId, formattedValues);
            message.success('Analyse foliaire mise à jour avec succès');
            navigate(`/admin/exploitation/${exploitationId}/analysis/feuilles`);
        } catch (error) {
            console.error('Erreur lors de la mise à jour de l\'analyse foliaire:', error);
            message.error('Échec de la mise à jour de l\'analyse foliaire');
        } finally {
            setLoading(false);
        }
    };

    const leafAnalysisParameters = [
        { key: 'n', label: 'Azote (N) % de MS' },
        { key: 'p', label: 'Phosphore (P) % de MS' },
        { key: 'k', label: 'Potassium (K) % de MS' },
        { key: 'mg', label: 'Magnésium (Mg) % de MS' },
        { key: 'ca', label: 'Calcium (Ca) % de MS' },
        { key: 'cl', label: 'Chlorures (Cl) % de MS' },
        { key: 'na', label: 'Sodium (Na) mg/kg de MS' },
        { key: 'zn', label: 'Zinc (Zn) mg/kg de MS' },
        { key: 'cu', label: 'Cuivre (Cu) mg/kg de MS' },
        { key: 'mn', label: 'Manganèse (Mn) mg/kg de MS' },
        { key: 'fe', label: 'Fer (Fe) mg/kg de MS' },
        { key: 'b', label: 'Bore (B) mg/kg de MS' },
    ];

    const HeaderTab = ({ text }) => (
        <th className="border border-gray-300 bg-gray-300 px-2 py-2">{text}</th>
    );


    return (
        <div className="bg-white h-full">
            <Header title="Modifier une Analyse Foliaire" />
            <div className="h-[calc(100%-100px)] p-6">
                <Link
                    to={`/admin/exploitation/${exploitationId}/analysis/feuilles`}
                    className="cursor-pointer flex pl-5 gap-2 mt-4"
                >
                    <img src={backIcon} alt="" className="w-5" />
                    <div className="font-semibold text-base text-indigo-600">Retour</div>
                </Link>
                {loading && <Loading />}
                <Form
                    form={form}
                    name="editLeafAnalysis"
                    onFinish={onFinish}
                    layout="vertical"
                    className='bg-white p-6 rounded-lg max-w-[1000px] shadow-md'
                    initialValues={initialData}
                >
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <Form.Item name="date_realisation" label="Date de réalisation" rules={[{ required: true }]}>
                            <CustomDatePicker />
                        </Form.Item>
                        <Form.Item name="laboratoire" label="Laboratoire">
                            <Input />
                        </Form.Item>
                        <Form.Item name="echantillon" label="Échantillon">
                            <Input />
                        </Form.Item>
                        <Form.Item name="leaf_name" label="Nom de la feuille">
                            <Input />
                        </Form.Item>
                        <Form.Item name="vanne_associee" label="Vanne associée">
                            <Input />
                        </Form.Item>
                    </div>

                    <h2 className="text-xl font-semibold mt-6 mb-4">Paramètres de l'analyse foliaire</h2>
                    <table className="w-full border-collapse border border-gray-300 mb-4">
                        <thead>
                            <tr>
                                <HeaderTab text="Paramètre" />
                                <HeaderTab text="Min" />
                                <HeaderTab text="Max" />
                                <HeaderTab text="Données" />
                            </tr>
                        </thead>
                        <tbody>
                            {leafAnalysisParameters.map(param => (
                                <tr key={param.key}>
                                    <td className="border border-gray-300 px-2 py-2">{param.label}</td>
                                    <td className="border border-gray-300 px-2 py-2">
                                        <Form.Item name={`${param.key}_min`} noStyle>
                                            <InputNumber step={0.01} style={{ width: '100%' }} />
                                        </Form.Item>
                                    </td>
                                    <td className="border border-gray-300 px-2 py-2">
                                        <Form.Item name={`${param.key}_max`} noStyle>
                                            <InputNumber step={0.01} style={{ width: '100%' }} />
                                        </Form.Item>
                                    </td>
                                    <td className="border border-gray-300 px-2 py-2">
                                        <Form.Item name={`${param.key}_data`} noStyle>
                                            <InputNumber step={0.01} style={{ width: '100%' }} />
                                        </Form.Item>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <Form.Item className="mt-4">
                        <Button type="primary" htmlType="submit" loading={loading}>
                            Mettre à jour l'Analyse Foliaire
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        </div>
    );
};

export default EditLeafAnalysis;